import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Drives a CSS scroll-snap track.
 *
 * The track itself does all the real work — native momentum, touch swipe and
 * keyboard scrolling come free from `overflow-x:auto` + `scroll-snap-type`.
 * This hook only adds what CSS cannot: the prev/next buttons, the active dot,
 * and disabling the arrows at either end.
 */
export function useCarousel() {
  const trackRef = useRef(null);
  const [index, setIndex] = useState(0);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const [count, setCount] = useState(0);

  const measure = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const items = [...el.children];
    setCount(items.length);

    // 1px of slack: sub-pixel layout means scrollLeft rarely hits the exact max.
    setAtStart(el.scrollLeft <= 1);
    setAtEnd(el.scrollLeft >= el.scrollWidth - el.clientWidth - 1);

    // Active item = whichever card's left edge is nearest the track's left edge.
    const left = el.scrollLeft;
    let nearest = 0;
    let best = Infinity;
    items.forEach((child, i) => {
      const d = Math.abs(child.offsetLeft - el.offsetLeft - left);
      if (d < best) {
        best = d;
        nearest = i;
      }
    });
    setIndex(nearest);
  }, []);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return undefined;
    measure();

    let frame = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    const ro = new ResizeObserver(measure);
    ro.observe(el);

    return () => {
      cancelAnimationFrame(frame);
      el.removeEventListener('scroll', onScroll);
      ro.disconnect();
    };
  }, [measure]);

  /** Distance of one card plus the flex gap. */
  const step = useCallback(() => {
    const el = trackRef.current;
    if (!el?.firstElementChild) return 0;
    const gap = parseFloat(getComputedStyle(el).columnGap || '0') || 0;
    return el.firstElementChild.getBoundingClientRect().width + gap;
  }, []);

  const scrollBy = useCallback(
    (dir) => trackRef.current?.scrollBy({ left: dir * step(), behavior: 'smooth' }),
    [step],
  );

  const next = useCallback(() => scrollBy(1), [scrollBy]);
  const prev = useCallback(() => scrollBy(-1), [scrollBy]);

  const goTo = useCallback((i) => {
    const el = trackRef.current;
    const child = el?.children?.[i];
    if (el && child) el.scrollTo({ left: child.offsetLeft - el.offsetLeft, behavior: 'smooth' });
  }, []);

  return { trackRef, index, count, atStart, atEnd, next, prev, goTo };
}
